import { useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { getApiHeaders, API_TYPES } from '../utils/apiHeaders';

const API_BASE_URL = import.meta.env.VITE_DB_API_URL || '/api';

/**
 * Hook to automatically populate OIDC identity claims on first login
 * This ensures users/admins transition from email-only to OIDC-based identification
 * 
 * IMPORTANT: This hook syncs identity every time subscriptionData changes,
 * including when switching between subscriptions. This ensures that if a user
 * is an admin in multiple subscriptions, their identity is synced for each one.
 */
export const useIdentitySync = (subscriptionData) => {
  const { isAuthenticated, getAccessToken, getUserEmail, getOIDCIdentityClaims } = useAuth();

  const syncIdentity = useCallback(async () => {
    if (!isAuthenticated || !subscriptionData) {
      return;
    }

    try {
      const token = await getAccessToken();
      const oidcClaims = getOIDCIdentityClaims();
      const currentEmail = getUserEmail();

      // Skip if we don't have valid OIDC claims
      if (!oidcClaims.subjectId || !oidcClaims.identityProvider) {
        console.log('OIDC claims not available, skipping identity sync');
        return;
      }

      // Get the current subscription ID
      const subscriptionId = subscriptionData.subscriptionID || subscriptionData.SubscriptionID;
      
      if (!subscriptionId) {
        console.warn('No subscription ID available for identity sync');
        return;
      }
      
      console.log(`Syncing identity for subscription: ${subscriptionId}`);

      // Check if current user is an admin in this subscription
      const currentAdmin = subscriptionData.admins?.find(admin => 
        (admin.email || admin.Email)?.toLowerCase() === currentEmail.toLowerCase()
      );

      if (currentAdmin) {
        // Check if admin already has identity claims populated
        const hasIdentity = (currentAdmin.identityProvider || currentAdmin.IdentityProvider) && 
                           (currentAdmin.subjectId || currentAdmin.SubjectId);
        
        if (hasIdentity) {
          console.log(`Admin identity already synced for subscription ${subscriptionId}`);
        } else {
          // Update admin identity - include subscriptionId in request
          const response = await fetch(`${API_BASE_URL}/UpdateAdminIdentity?subscriptionId=${subscriptionId}`, {
            method: 'PUT',
            headers: getApiHeaders(token, API_TYPES.DATABASE),
            body: JSON.stringify({
              email: currentEmail,
              identityProvider: oidcClaims.identityProvider,
              subjectId: oidcClaims.subjectId,
              subscriptionID: subscriptionId  // Include in body as well for backend compatibility
            })
          });

          if (response.ok) {
            const result = await response.json();
            console.log(`Admin identity synced for subscription ${subscriptionId}:`, result.message);
          } else {
            console.warn(`Failed to sync admin identity for subscription ${subscriptionId}:`, await response.text());
          }
        }
      }

      // Also check if user exists in this subscription (admins can be users too)
      const currentUser = subscriptionData.users?.find(user => 
        (user.email || user.Email)?.toLowerCase() === currentEmail.toLowerCase()
      );

      if (currentUser) {
        // Check if user already has identity claims populated
        const hasIdentity = (currentUser.identityProvider || currentUser.IdentityProvider) && 
                           (currentUser.subjectId || currentUser.SubjectId);
        
        if (hasIdentity) {
          console.log(`User identity already synced for subscription ${subscriptionId}`);
        } else {
          // Update user identity - include subscriptionId in request
          const response = await fetch(`${API_BASE_URL}/UpdateUserIdentity?subscriptionId=${subscriptionId}`, {
            method: 'PUT',
            headers: getApiHeaders(token, API_TYPES.DATABASE),
            body: JSON.stringify({
              email: currentEmail,
              identityProvider: oidcClaims.identityProvider,
              subjectId: oidcClaims.subjectId,
              subscriptionID: subscriptionId  // Include in body as well for backend compatibility
            })
          });

          if (response.ok) {
            const result = await response.json();
            console.log(`User identity synced for subscription ${subscriptionId}:`, result.message);
          } else {
            console.warn(`Failed to sync user identity for subscription ${subscriptionId}:`, await response.text());
          }
        }
      }
    } catch (error) {
      console.error('Error syncing identity:', error);
      // Don't throw - this is a background enhancement, shouldn't break the app
    }
  }, [isAuthenticated, subscriptionData, getAccessToken, getUserEmail, getOIDCIdentityClaims]);

  // Sync identity when subscription data loads or changes (including subscription switches)
  // Use the subscription ID as a key to ensure sync runs for each unique subscription
  useEffect(() => {
    if (subscriptionData && isAuthenticated) {
      const subscriptionId = subscriptionData.subscriptionID || subscriptionData.SubscriptionID;
      
      // Small delay to ensure all data is loaded
      const timer = setTimeout(() => {
        syncIdentity();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [
    subscriptionData?.subscriptionID, 
    subscriptionData?.SubscriptionID, 
    isAuthenticated, 
    syncIdentity
  ]);

  return { syncIdentity };
};
